import { heraldQuestionId } from '@common/types';
import {
  HeraldApiError,
  HeraldApplication,
  HeraldApplicationResponse,
  HeraldIndexEntry,
  HeraldUpdateApplication,
  QuoteExits,
} from '@common/types/herald/herald-types';
import { convertCoverageValueToLean, convertRiskValueToLean, replaceByKey } from '@common/utils';
import { useEffect, useMemo, useState } from 'react';
import { ForbiddenError } from 'clients/errors/ForbiddenError';
import { useHeraldApplicationApi, useHeraldIndustryClassificationApi, useRetrieveHeraldApplication } from 'hooks';
import { messages } from 'i18n';
import { PartialSubmission } from 'types';
import { logger } from 'utils';
import { DynamicFormState } from 'broker/pages/SubmissionWorkspacePage/components/DynamicForm/DynamicFormLayout/types';
import { DynamicFormFieldApiError } from 'broker/pages/SubmissionWorkspacePage/components/DynamicForm/types';
import { FormMode } from 'broker/pages/SubmissionWorkspacePage/components/DynamicForm/useGetDynamicFormDisplayProps';
import heraldApplicationNormalizer from './mappers/herald-application-normalizer';
import mapFormStateToHerald from './mappers/map-form-state-to-herald';
import { HeraldApplicationNormalized } from './types';
import { getHeraldServerErrorFieldMessage } from './utils';

interface HeraldProviderProps {
  submission: PartialSubmission;
  isHeraldSubmission: boolean;
  vipQuestions: string[];
}

export function useGetHeraldProvider({ submission, isHeraldSubmission, vipQuestions }: HeraldProviderProps) {
  const heraldApplicationId = isHeraldSubmission ? submission.heraldData?.applicationId : undefined;
  const [heraldApplication, setHeraldApplication] = useState<HeraldApplication | undefined>();
  const [applicationIndustryDetails, setApplicationIndustryDetails] = useState<undefined | HeraldIndexEntry | null>();
  const [quoteExits, setQuoteExists] = useState<QuoteExits[] | undefined>();
  const [isLoadingHeraldApplication, setIsLoadingHeraldApplication] = useState(!!heraldApplicationId);

  const { refetch: fetchHeraldApplication } = useRetrieveHeraldApplication({
    id: heraldApplicationId!,
    enabled: false,
  });

  // if this is a herald submission, we start with loading the herald application
  const { upsertApplication } = useHeraldApplicationApi();

  const { retrieveIndustryClassificationByHeraldId } = useHeraldIndustryClassificationApi();

  const heraldApplicationNormalized: HeraldApplicationNormalized | undefined = useMemo(
    () =>
      heraldApplication
        ? heraldApplicationNormalizer(heraldApplication, vipQuestions, applicationIndustryDetails || undefined)
        : undefined,
    [applicationIndustryDetails, heraldApplication, vipQuestions],
  );

  const heraldDynamicQuestions = useMemo(
    () => [
      ...(heraldApplicationNormalized?.risk_values || []),
      ...(heraldApplicationNormalized?.coverage_values || []),
    ],
    [heraldApplicationNormalized?.coverage_values, heraldApplicationNormalized?.risk_values],
  );

  const updateHeraldApplicationStates = (
    heraldApplicationItem: HeraldApplication,
    industryIndexEntry: HeraldIndexEntry | null,
    heraldQuoteExits?: QuoteExits[],
  ) => {
    setApplicationIndustryDetails(industryIndexEntry);
    setQuoteExists(heraldQuoteExits);
    setHeraldApplication(heraldApplicationItem);
    setIsLoadingHeraldApplication(false);
  };

  const getIndustryIndexEntry = async (heraldApplicationItem: HeraldApplication) => {
    let industryIndexEntry: HeraldIndexEntry | null = null;
    const industry = heraldApplicationItem.risk_values.find(
      (riskValue) => riskValue.risk_parameter_id === heraldQuestionId.rsk_b3jm_2017_naics_index,
    );
    if (industry?.value) {
      industryIndexEntry = await retrieveIndustryClassificationByHeraldId(industry.value);
    }

    return industryIndexEntry;
  };

  useEffect(() => {
    if (heraldApplicationId) {
      fetchHeraldApplication().then(async (response) => {
        if (response.data) {
          const industryIndexEntry = await getIndustryIndexEntry(response.data.application);
          updateHeraldApplicationStates(response.data.application, industryIndexEntry, response.data.quote_exits);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heraldApplicationId, fetchHeraldApplication]);

  const mergeFormStateToHeraldWithCurrentApplication = (
    formStateToHerald: Omit<HeraldUpdateApplication, 'products'>,
  ) => {
    const currentRiskValues = heraldApplication?.risk_values.map(convertRiskValueToLean) || [];

    const currentCoverageValues = heraldApplication?.coverage_values.map(convertCoverageValueToLean) || [];

    return {
      risk_values: replaceByKey(currentRiskValues, formStateToHerald.risk_values || [], 'risk_parameter_id'),
      coverage_values: replaceByKey(
        currentCoverageValues,
        formStateToHerald.coverage_values || [],
        'coverage_parameter_id',
      ),
    };
  };

  const updateHeraldWithFormValues = async (
    formState: DynamicFormState,
    formMode?: FormMode,
  ): Promise<HeraldApplicationResponse | undefined> => {
    const formStateToHerald = mapFormStateToHerald(formState, heraldDynamicQuestions);
    try {
      const response = await upsertApplication.mutateAsync({
        existingApplicationId: heraldApplication!.id,
        upsertData: {
          products: heraldApplication!.products,
          ...(formMode === FormMode.Minimal
            ? mergeFormStateToHeraldWithCurrentApplication(formStateToHerald)
            : formStateToHerald),
        },
        retryAfterValidationError: false,
      });
      if (response?.application) {
        const industryIndexEntry = await getIndustryIndexEntry(response.application);
        updateHeraldApplicationStates(response.application, industryIndexEntry, response.quote_exits);
        return response;
      }
      return undefined;
    } catch (e: any) {
      if (!(e instanceof ForbiddenError)) {
        const errorFieldMessage = getHeraldServerErrorFieldMessage(e as HeraldApiError, formStateToHerald);
        if (errorFieldMessage) {
          throw new DynamicFormFieldApiError(errorFieldMessage.fieldName, errorFieldMessage.message);
        }
      }
      logger.log('error', { message: messages.heraldForm.failedToUpdateApplication, error: e, formState });
      throw e;
    }
  };

  return {
    isLoadingHeraldApplication,
    heraldDynamicQuestions,
    updateHeraldWithFormValues,
    heraldProducts: heraldApplication?.products,
    quoteExits,
  };
}
